import React, { useRef, useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import ActionSheet from '../../../../components/mobile/ActionSheet';
import 'yet-another-react-lightbox/styles.css';

const PRIMARY = '#FF7E67';

export default function ImageViewerModal({
  isOpen,
  images = [],
  inputImages = [],
  imageParams = [],
  initialIndex = 0,
  /** Open straight into the before/after slider (the card's Compare action). */
  initialCompare = false,
  onClose,
  onUseAsInput,
  onDownload
}) {
  const indexRef = useRef(initialIndex);
  const [compareMode, setCompareMode] = useState(initialCompare);
  const [compareIndex, setCompareIndex] = useState(0);
  const [showParamSheet, setShowParamSheet] = useState(false);

  // Each open is a fresh intent — re-seed both the slide and the compare mode.
  React.useEffect(() => {
    if (!isOpen) return;
    indexRef.current = initialIndex;
    setCompareMode(initialCompare && inputImages.length > 0);
  }, [isOpen, initialIndex, initialCompare, inputImages.length]);

  const slides = useMemo(() => images.map(src => ({ src })), [images]);

  const zoomConfig = useMemo(() => ({
    maxZoomPixelRatio: 3,
    zoomInMultiplier: 2,
    doubleTapDelay: 300,
    doubleClickMaxStops: 2,
    scrollToZoom: true
  }), []);

  const carouselConfig = useMemo(() => ({
    finite: images.length <= 1
  }), [images.length]);

  const stylesConfig = useMemo(() => ({
    container: { 
      backgroundColor: 'rgba(0, 0, 0, 0.98)',
      zIndex: 99999
    }
  }), []);

  const plugins = useMemo(() => [Zoom], []);

  const handleView = useCallback(({ index }) => {
    indexRef.current = index;
  }, []);

  // Get file extension from mime type or URL
  const getFileExtension = useCallback((mimeType, url) => {
    if (mimeType && mimeType.includes('/')) {
      const ext = mimeType.split('/')[1].split(';')[0];
      if (ext === 'jpeg') return 'jpg';
      if (ext === 'quicktime') return 'mov';
      return ext;
    }
    const match = url.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
    return match ? match[1] : 'png';
  }, []);

  const handleDownload = useCallback(async () => {
    const url = images[indexRef.current];
    if (!url) return;
    
    if (onDownload) {
      onDownload(url);
    } else {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const ext = getFileExtension(blob.type, url);
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `image_${Date.now()}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (err) {
        // Fallback: try direct link
        const ext = getFileExtension('', url);
        const a = document.createElement('a');
        a.href = url;
        a.download = `image_${Date.now()}.${ext}`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    }
  }, [images, onDownload, getFileExtension]);

  const handleUseAsInput = useCallback(() => {
    const url = images[indexRef.current];
    if (!url || !onUseAsInput) return;
    
    if (imageParams.length > 1) {
      setShowParamSheet(true);
    } else {
      onUseAsInput(url);
      onClose();
    }
  }, [images, onUseAsInput, onClose, imageParams.length]);

  const handleToggleCompare = useCallback(() => {
    setCompareMode(prev => !prev);
  }, []);

  // Handle ESC key to only close this modal, not parent
  React.useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen || images.length === 0) return null;

  // Compare mode - show before/after slider
  if (compareMode && inputImages.length > 0) {
    return createPortal(
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.98)',
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          paddingTop: 'max(12px, env(safe-area-inset-top))',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <button
            onClick={onClose}
            style={{
              width: 40, height: 40, borderRadius: 8,
              background: 'rgba(255,255,255,0.1)', border: 'none',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <span style={{ color: '#9ca3af', fontSize: 14 }}>Before / After</span>
          <button
            onClick={handleToggleCompare}
            style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'rgba(59, 130, 246, 0.2)', border: 'none',
              color: '#60a5fa', fontSize: 13, cursor: 'pointer'
            }}
          >
            Exit Compare
          </button>
        </div>

        {/* Compare slider */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: 16,
          overflow: 'hidden'
        }}>
          <div style={{ 
            width: '100%', 
            maxWidth: 900, 
            height: '100%',
            maxHeight: 'calc(100vh - 140px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <ReactCompareSlider
              itemOne={
                <ReactCompareSliderImage 
                  src={inputImages[compareIndex]} 
                  alt="Before" 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }} 
                />
              }
              itemTwo={
                <ReactCompareSliderImage 
                  src={images[indexRef.current] || images[0]} 
                  alt="After" 
                  style={{ 
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }} 
                />
              }
              style={{ 
                borderRadius: 12,
                width: '100%',
                height: '100%',
                maxHeight: 'calc(100vh - 160px)'
              }}
            />
          </div>
        </div>

        {/* Input image selector if multiple */}
        {inputImages.length > 1 && (
          <div style={{
            display: 'flex',
            gap: 8,
            padding: '12px 16px',
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            justifyContent: 'center',
            borderTop: '1px solid rgba(255,255,255,0.1)'
          }}>
            <span style={{ color: '#6b7280', fontSize: 12, marginRight: 8 }}>Compare with:</span>
            {inputImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCompareIndex(idx)}
                style={{
                  width: 44, height: 44, borderRadius: 8, padding: 0,
                  border: idx === compareIndex ? `2px solid ${PRIMARY}` : '2px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer', overflow: 'hidden',
                  opacity: idx === compareIndex ? 1 : 0.6
                }}
              >
                <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </button>
            ))}
          </div>
        )}
      </div>,
      document.body
    );
  }

  // Normal lightbox mode
  return createPortal(
    <>
    <Lightbox
      open={isOpen}
      close={onClose}
      index={initialIndex}
      slides={slides}
      on={{ view: handleView }}
      plugins={plugins}
      zoom={zoomConfig}
      carousel={carouselConfig}
      controller={{ closeOnBackdropClick: true, closeOnPullDown: false }}
      keyboard={{ global: false }}
      styles={stylesConfig}
      toolbar={{
        buttons: [
          // Download button
          <button
            key="download"
            type="button"
            onClick={handleDownload}
            style={{
              background: 'transparent', border: 'none',
              color: '#fff', cursor: 'pointer', padding: 8
            }}
            title="Download"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>,
          // Use as input button
          onUseAsInput && (
            <button
              key="use"
              type="button"
              onClick={handleUseAsInput}
              style={{
                background: `${PRIMARY}20`, 
                border: `1px solid ${PRIMARY}40`,
                borderRadius: 6,
                color: PRIMARY, 
                cursor: 'pointer', 
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              title="Use as Input"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {imageParams.length > 1 ? 'Use as…' : 'Use'}
            </button>
          ),
          // Compare button (only if input images exist)
          inputImages.length > 0 && (
            <button
              key="compare"
              type="button"
              onClick={handleToggleCompare}
              style={{
                background: 'transparent', border: 'none',
                color: '#60a5fa', cursor: 'pointer', padding: 8
              }}
              title="Compare Before/After"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="12" y1="3" x2="12" y2="21" />
              </svg>
            </button>
          ),
          'close'
        ].filter(Boolean)
      }}
    />
    
    {/* ActionSheet for selecting image param when multiple exist */}
    <ActionSheet
      isOpen={showParamSheet && imageParams.length > 1}
      onClose={() => setShowParamSheet(false)}
      title="Use as input for"
      options={imageParams.map(param => ({
        label: param.title,
        value: param.paramName,
      }))}
      onSelect={(paramName) => {
        const url = images[indexRef.current];
        if (url) {
          onUseAsInput(url, paramName);
          onClose();
        }
        setShowParamSheet(false);
      }}
    />
    </>,
    document.body
  );
}
